import React, { createContext, useContext, useState, useEffect } from 'react';

interface QuickAddContextType {
  isQuickAddOpen: boolean;
  setIsQuickAddOpen: (open: boolean) => void;
}

const QuickAddContext = createContext<QuickAddContextType | undefined>(undefined);

export const QuickAddProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+N (or Cmd+N on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        // Prevent default browser behavior (new window/tab)
        event.preventDefault();
        
        // Only open if not already open and not in an input field
        const activeElement = document.activeElement;
        const isInputField = activeElement?.tagName === 'INPUT' || 
                           activeElement?.tagName === 'TEXTAREA' || 
                           (activeElement as HTMLElement)?.contentEditable === 'true';
        
        if (!isInputField && !isQuickAddOpen) {
          setIsQuickAddOpen(true);
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isQuickAddOpen]);

  return (
    <QuickAddContext.Provider value={{ isQuickAddOpen, setIsQuickAddOpen }}>
      {children}
    </QuickAddContext.Provider>
  );
};

export const useQuickAdd = () => {
  const context = useContext(QuickAddContext);
  if (context === undefined) {
    throw new Error('useQuickAdd must be used within a QuickAddProvider');
  }
  return context;
};